import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, of, tap } from 'rxjs';

import { UrlService } from 'app/core/http/url.service';
import { CountryModel } from 'app/shared/models/country.model';
import { RegionInfo } from 'app/shared/models/region-code.model';
import { TimeZoneModel } from '../models/time-zone.model';

@Injectable({
  providedIn: 'root'
})
export class LocationService {
  private readonly httpClient = inject(HttpClient);
  private readonly urlService = inject(UrlService);

  private _countries: CountryModel[] | null = null;

  public getLocationFromIp(): Observable<RegionInfo> {
    const cachedLocation = sessionStorage.getItem('LocationService.LocationFromIp');
    if (cachedLocation) {
      return of(JSON.parse(cachedLocation) as RegionInfo);
    }

    return this.httpClient.get<RegionInfo>(this.urlService.buildServerApiUrl('Account/CountryCode'))
      .pipe(tap((data) => { sessionStorage.setItem('LocationService.LocationFromIp', JSON.stringify(data)); }));
  }

  public getAllCountries(localeId: string): Observable<CountryModel[]> {
    const cacheKey = `LocationService.AllCountries_${localeId}`;
    const cachedCountries = sessionStorage.getItem(cacheKey);
    if (cachedCountries) {
      return of(JSON.parse(cachedCountries) as CountryModel[]);
    }

    const params = new HttpParams().set('localeId', localeId);

    return this.httpClient.get<CountryModel[]>(this.urlService.buildServerApiUrl('Countries/All'), { params })
      .pipe(tap((data) => { sessionStorage.setItem(cacheKey, JSON.stringify(data)); }));
  }

  public getCountries(localeId: string): Observable<CountryModel[]> {
    if (this._countries) {
      return of(this._countries);
    }

    const cacheKey = `LocationService.Countries_${localeId}`;
    const cachedCountries = sessionStorage.getItem(cacheKey);
    if (cachedCountries) {
      this._countries = JSON.parse(cachedCountries) as CountryModel[];

      return of(this._countries);
    }

    const params = new HttpParams().set('localeId', localeId);

    return this.httpClient.get<CountryModel[]>(this.urlService.buildServerApiUrl('Countries/all'), { params })
      .pipe(tap((data) => {
        this._countries = data;
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      }));
  }

  public getTimezones(): Observable<TimeZoneModel[]> {
    const cacheKey = 'LocationService.Timezones';
    const cachedTimezones = sessionStorage.getItem(cacheKey);
    if (cachedTimezones) {
      return of(JSON.parse(cachedTimezones) as TimeZoneModel[]);
    }

    const url = this.urlService.buildServerApiUrl('timezones');
    return this.httpClient.get<TimeZoneModel[]>(url)
      .pipe(tap((data) => {
        sessionStorage.setItem(cacheKey, JSON.stringify(data));
      }));
  }
}
